import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  CircularProgress,
} from '@mui/material';
import { 
  AdminPanelSettings, 
  Security, 
  Notifications, 
  Storage,
  Backup,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { featureFlagsService } from '../services/featureFlagsService';
import { TextSizeControl } from '../components/accessibility/TextSizeControl';

export const SettingsPage: React.FC = () => {
  const { state, dispatch } = useApp();
  const navigate = useNavigate();
  const [createAccountEnabled, setCreateAccountEnabled] = useState<boolean>(true);
  const [isLoadingFeatureFlags, setIsLoadingFeatureFlags] = useState<boolean>(true);
  const [isSavingFeatureFlags, setIsSavingFeatureFlags] = useState<boolean>(false);
  const [featureFlagsError, setFeatureFlagsError] = useState<string | null>(null);
  const [featureFlagsSuccess, setFeatureFlagsSuccess] = useState<string | null>(null);

  const handleThemeToggle = () => {
    const newTheme = state.theme === 'light' ? 'dark' : 'light';
    dispatch({ type: 'SET_THEME', payload: newTheme });
  };

  useEffect(() => {
    const loadFeatureFlags = async () => {
      setIsLoadingFeatureFlags(true);
      setFeatureFlagsError(null);

      try {
        const flags = await featureFlagsService.getFeatureFlags();
        setCreateAccountEnabled(flags.create_account_enabled);
      } catch (error) {
        console.error('Failed to load feature flags:', error);
        setFeatureFlagsError('Unable to load feature flags right now.');
      } finally {
        setIsLoadingFeatureFlags(false);
      }
    };

    loadFeatureFlags();
  }, []);

  const handleCreateAccountToggle = async (checked: boolean) => {
    setIsSavingFeatureFlags(true);
    setFeatureFlagsError(null);
    setFeatureFlagsSuccess(null);

    try {
      const updatedFlags = await featureFlagsService.updateFeatureFlags({
        create_account_enabled: checked,
      });
      setCreateAccountEnabled(updatedFlags.create_account_enabled);
      setFeatureFlagsSuccess('Feature flags updated successfully.');
    } catch (error) {
      console.error('Failed to update feature flags:', error);
      setFeatureFlagsError('Failed to update feature flags.');
    } finally {
      setIsSavingFeatureFlags(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {featureFlagsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {featureFlagsError}
            </Alert>
          )}
          {featureFlagsSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {featureFlagsSuccess}
            </Alert>
          )}

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AdminPanelSettings sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h4" gutterBottom>
                  Admin Settings
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary">
                Manage your admin preferences and system configuration
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Feature Flags
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Toggle runtime product capabilities without redeploying clients.
              </Typography>
              {isLoadingFeatureFlags ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading feature flags...
                  </Typography>
                </Box>
              ) : (
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Create Account"
                      secondary="Controls whether Create Account entry points are visible in client apps."
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        edge="end"
                        checked={createAccountEnabled}
                        onChange={(event) => handleCreateAccountToggle(event.target.checked)}
                        disabled={isSavingFeatureFlags}
                        inputProps={{ 'aria-label': 'toggle create account feature flag' }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Appearance
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Text Size"
                    secondary="Adjust text size across the admin dashboard"
                  />
                  <ListItemSecondaryAction>
                    <TextSizeControl
                      value={state.textSize}
                      onChange={(size) => dispatch({ type: 'SET_TEXT_SIZE', payload: size })}
                      compact
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Dark Mode"
                    secondary="Switch between light and dark themes"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      edge="end"
                      checked={state.theme === 'dark'}
                      onChange={handleThemeToggle}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
                Security
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Two-Factor Authentication"
                    secondary="Add an extra layer of security"
                  />
                  <ListItemSecondaryAction>
                    <Button variant="outlined" size="small">
                      Enable
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Session Timeout"
                    secondary="Auto-logout after inactivity"
                  />
                  <ListItemSecondaryAction>
                    <Switch edge="end" defaultChecked />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Notifications sx={{ mr: 1, verticalAlign: 'middle' }} />
                Notifications
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Email Notifications"
                    secondary="Receive admin alerts via email"
                  />
                  <ListItemSecondaryAction>
                    <Switch edge="end" defaultChecked />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="System Alerts"
                    secondary="Get notified of system issues"
                  />
                  <ListItemSecondaryAction>
                    <Switch edge="end" defaultChecked />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Storage sx={{ mr: 1, verticalAlign: 'middle' }} />
                Data Management
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Auto Backup"
                    secondary="Automatically backup system data"
                  />
                  <ListItemSecondaryAction>
                    <Switch edge="end" defaultChecked />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Data Retention"
                    secondary="Keep user data for 2 years"
                  />
                  <ListItemSecondaryAction>
                    <Button variant="outlined" size="small">
                      Configure
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
                Admin Tools
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button variant="outlined">
                  <Backup sx={{ mr: 1 }} />
                  System Backup
                </Button>
                <Button variant="outlined">
                  Database Maintenance
                </Button>
                <Button variant="outlined">
                  User Management
                </Button>
                <Button variant="outlined">
                  System Logs
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Account Management
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Profile"
                    secondary="Manage your admin profile information"
                  />
                  <ListItemSecondaryAction>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate('/admin/profile')}
                    >
                      View
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Change Password"
                    secondary="Update your admin password"
                  />
                  <ListItemSecondaryAction>
                    <Button variant="outlined" size="small">
                      Change
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Legal
              </Typography>
              <Typography variant="body2" paragraph>
                Published policies live on the public ShareGiver site.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  href="https://sharegiver.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="admin-legal-privacy"
                >
                  Privacy Policy
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  href="https://sharegiver.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="admin-legal-terms"
                >
                  Terms of Service
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  href="https://sharegiver.com/eula"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="admin-legal-eula"
                >
                  EULA
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  href="https://sharegiver.com/dmca"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="admin-legal-dmca"
                >
                  DMCA
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                About ShareGiver Admin
              </Typography>
              <Typography variant="body2" paragraph>
                ShareGiver Admin Dashboard v1.0.0
              </Typography>
              <Typography variant="body2" paragraph>
                Administrative interface for the ShareGiver platform. Built with React, Material-UI, and Firebase integration.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button variant="outlined" size="small">
                  View Documentation
                </Button>
                <Button variant="outlined" size="small">
                  Report Issue
                </Button>
                <Button variant="outlined" size="small">
                  Check for Updates
                </Button>
                <Button variant="outlined" size="small">
                  System Status
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
